import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { deleteStudent, getAllStudent, getUserToken, userEditData } from "../../redux/services/studentSlice";
import { MdBlock } from "react-icons/md";
import { AiOutlineDelete, AiOutlineEye } from "react-icons/ai";
import { toast } from "react-toastify";
import ConfirmationPage from "../../common/ConfirmationPage";
import Table from "../../common/Table";
import fallbackCopyText from "../../common/copyToText";
import { exportToTableCSV, exportToTablePDF } from "../../common/PdfFormat";
import { convertUtcToIstDateOnly } from "../../common/TimeUtils";
import { LuPencilLine } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import { TbAccessPoint } from "react-icons/tb";

const Student = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchData, setSearchData] = useState("");
  const [filter, setFilter] = useState("");
  const [itemToDelete, setItemToDelete] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState([]);

  const limitOptions = [
    { value: 10, label: 10 },
    { value: 30, label: 30 },
    { value: 50, label: 50 },
    { value: 100, label: 100 },
  ];
  const [pageLimit, setPageLimit] = useState(limitOptions[0]?.value);
  const getStudentData = useSelector((state) => state.student.studentData);

  const getAllStudentApi = useCallback(async () => {
    try {
      await dispatch(
        getAllStudent({
          page: page + 1,
          limit: pageLimit,
          searchData,
          blockFilter: filter,
        })
      );
    } catch (error) {}
  }, [dispatch, page, pageLimit, searchData, filter]);

  useEffect(() => {
    getAllStudentApi();
  }, [getAllStudentApi]);

  const deleteStudentData = async () => {
    try {
      const response = await dispatch(deleteStudent(itemToDelete));
      toast.success(response?.payload?.message, {
        autoClose: 2000,
        pauseOnHover: false,
      });
      setConfirm(false);
      getAllStudentApi();
    } catch (error) {}
  };

  const editUser = async (userId, isblocked) => {
    let payload = {
      userId: userId,
      isBlocked: isblocked,
    };
    try {
      const response = await dispatch(userEditData(payload));
      if (response) {
        getAllStudentApi();
      }
    } catch (error) {}
  };
  const exportToPDF = () => {
    const headersConfig = [
      { label: "S/N", key: "index", render: (_, __, index) => index + 1 },
      {
        label: "Name",
        key: `"firstName"`,
        render: (_, row) => `${row?.firstName + " " + row?.lastName}` || "-",
      },
      { label: "Email", key: "email", render: (_, row) => row?.email || "-" },
      {
        label: "DOB",
        key: "dob",
        render: (_, row) => convertUtcToIstDateOnly(row?.dob) || "-",
      },
      {
        label: "Ph No.",
        key: "contact",
        render: (_, row) => (row?.contact ? row?.contact?.countryCode + " " + row?.contact?.mobile : "-"),
      },
      {
        label: "Gender",
        key: "gender",
        render: (_, row) => row?.gender || "-",
      },
      { label: "City", key: "city", render: (_, row) => row?.city || "-" },
      {
        label: "Ref Code",
        key: "referralCode",
        render: (_, row) => row?.referralCode || "-",
      },
      {
        label: "Password",
        key: "showPassword",
        render: (_, row) => row?.showPassword || "-",
      },
      {
        label: "Friend Ref Code",
        key: "referralCode",
        render: (_, row) => row?.friendReferralCode || "-",
      },
      {
        label: "Balance",
        key: "walletBalance",
        render: (_, row) => row?.walletBalance.toFixed(2) || "-",
      },
    ];
    const data = getStudentData?.subject_data || [];
    exportToTablePDF({
      tableName: "Students",
      data,
      selectedStudents,
      headersConfig,
      pdfName: "student_data.pdf",
    });
  };
  const exportToCSV = () => {
    const headersConfig = [
      { label: "S/N", key: "index", render: (_, __, index) => index + 1 },
      {
        label: "Name",
        key: `"firstName"`,
        render: (_, row) => `${row?.firstName + " " + row?.lastName}` || "-",
      },
      { label: "Email", key: "email", render: (_, row) => row?.email || "-" },
      {
        label: "DOB",
        key: "dob",
        render: (_, row) => {
          return convertUtcToIstDateOnly(row?.dob) || "-";
        },
      },
      {
        label: "Ph No.",
        key: "contact",
        render: (_, row) => (row?.contact ? row?.contact?.countryCode + " " + row?.contact?.mobile : "-"),
      },
      {
        label: "Gender",
        key: "gender",
        render: (_, row) => row?.gender || "-",
      },
      { label: "City", key: "city", render: (_, row) => row?.city || "-" },
      {
        label: "Ref Code",
        key: "referralCode",
        render: (_, row) => row?.referralCode || "-",
      },
      {
        label: "Password",
        key: "showPassword",
        render: (_, row) => row?.showPassword || "-",
      },
      {
        label: "Friend Ref Code",
        key: "referralCode",
        render: (_, row) => row?.friendReferralCode || "-",
      },
      {
        label: "Balance",
        key: "walletBalance",
        render: (_, row) => row?.walletBalance.toFixed(2) || "-",
      },
    ];
    const data = getStudentData?.subject_data || [];
    exportToTableCSV({
      data,
      selectedStudents,
      headersConfig,
      csvName: "student_data.csv",
    });
  };

  const columns = [
    {
      label: "Select",
      key: "select",
      render: (_, row) => <input type="checkbox" checked={selectedStudents.includes(row._id)} onChange={() => handleCheckboxChange(row._id)} />,
    },
    {
      label: "S/N",
      key: "index",
      render: (_, __, index) => page * pageLimit + index + 1,
    },
    {
      label: "Copy ID",
      key: "index",
      render: (_, row) => (
        <button
          className="py-1 px-3 rounded-xl text-white bg-accent font-semibold"
          onClick={() => {
            if (navigator.clipboard && navigator.clipboard.writeText) {
              navigator.clipboard
                .writeText(row?._id)
                .then(() => {
                  toast.success("ID copied to clipboard!", {
                    autoClose: 2000,
                    pauseOnHover: false,
                  });
                })
                .catch((err) => {
                  fallbackCopyText(row?._id);
                });
            } else {
              fallbackCopyText(row?._id);
            }
          }}
        >
          Copy
        </button>
      ),
    },
    {
      label: "Name",
      key: "userName",
      render: (_, row) => {
        if (!row) {
          return "-";
        }
        return `${row?.firstName + " " + row?.lastName}`;
      },
    },
    {
      label: "Email",
      key: "email",
      render: (_, row) => row?.email || "-",
    },
    {
      label: "City",
      key: "contact",
      render: (_, row) => row?.city || "-",
    },
    {
      label: "Ref Code",
      key: "referral-code",
      render: (_, row) => row?.referralCode || "-",
    },
    {
      label: "Password",
      key: "showPassword",
      render: (_, row) => row?.showPassword || "-",
    },
    {
      label: "Balance",
      key: "balance",
      render: (_, row) => row?.walletBalance.toFixed(2) || "-",
    },
    {
      label: "Image",
      key: "balance",
      render: (_, row) =>
        row?.profileImage && row?.profileImage !== "string" ? (
          <div className="flex justify-center h-16 w-20">
            <img src={row?.profileImage} alt="" className="h-full w-full" />
          </div>
        ) : (
          "—"
        ),
    },
  ];
  const getUserData = async (userId) => {
    try {
      const response = await dispatch(getUserToken(userId));
      if (response?.payload?.status === 200) {
        window.open(`${process.env.REACT_APP_USER_PANEL_URL}?token=${response.payload?.data}&userId=${userId}`);
      }
    } catch (error) {}
  };
  const actions = [
    {
      icon: (row) => <MdBlock className={`${row.isBlocked ? "text-green-600" : "text-red-600"} w-6 h-6 cursor-pointer`} />,
      onClick: (row) => editUser(row._id, !row.isBlocked),
    },
    {
      icon: (row) => <TbAccessPoint className={` w-6 h-6 cursor-pointer`} />,
      onClick: (row) => getUserData(row?._id),
    },
    {
      icon: () => <AiOutlineEye className="w-5 h-5 cursor-pointer text-primary" />,
      onClick: (row) => {
        navigate(`/user-detail?studentId=${row._id}`);
      },
    },
    {
      icon: () => <LuPencilLine className="w-5 h-5" />,
      onClick: (row) => {
        navigate(`/edit-student?studentId=${row._id}`);
        // setModelOpen(true);
        // setEditId(row._id);
      },
    },
    {
      icon: () => <AiOutlineDelete className="w-5 h-5 cursor-pointer" />,
      onClick: (row) => {
        setItemToDelete(row._id);
        setConfirm(true);
      },
    },
  ];

  const filterOptions = [
    { value: "", label: "All" },
    { value: "false", label: "Accept" },
    { value: "true", label: "Block" },
  ];

  const handleCheckboxChange = (id) => {
    setSelectedStudents((prev) => {
      if (prev.includes(id)) {
        return prev.filter((studentId) => studentId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  return (
    <div>
      <div className="flex justify-end"></div>
      <Table title="Students" showSearchbar={true} searchTerm={searchTerm} setSearchTerm={setSearchTerm} setSearchData={setSearchData} filterOptions={filterOptions} selectedFilter={filter} onFilterChange={(e) => setFilter(e ? e.value : "")} columns={columns} data={getStudentData?.subject_data} actions={actions} pageCount={getStudentData?.state?.page_limit} pageLimitOptions={limitOptions} selectedPageLimit={pageLimit} onPageLimitChange={setPageLimit} onPageChange={(selectedItem) => setPage(selectedItem.selected)} exportButton={true} onExport={exportToPDF} exportCsv={true} onExportCSV={exportToCSV} />

      {confirm && <ConfirmationPage topicName="Student" confirm={confirm} onDelete={deleteStudentData} onCancel={() => setConfirm(false)} />}
    </div>
  );
};

export default Student;
